(() => {
  if (customElements.get('wb-loop-bundle')) return;

  const detailsCache = new Map();
  const scriptData = document.currentScript?.dataset || {};
  const validId = id => /^([0-9a-f]{32}|[0-9A-Z]{26})$/.test(id || '');
  const root = () => window.Shopify?.routes?.root || '/';
  let removing = false;
  let busy = false;
  let recoveryStarted = false;
  const pendingKey = 'wb-loop-purchase-pending';
  const purchaseCache = new Map();
  const disabledControls = new Map();
  const cartArea = '#t4s-mini_cart, [data-cart-items], form[action*="/cart"]';
  const lock = value => {
    busy = value;
    document.documentElement.classList.toggle('wb-loop-cart-busy', value);
    document.querySelectorAll('wb-loop-bundle').forEach(el => el.setAttribute('aria-busy', String(value)));
    if (value) {
      document.querySelectorAll(`${cartArea}`).forEach(area => area.querySelectorAll('input, select, button').forEach(control => {
        if (!disabledControls.has(control)) disabledControls.set(control, control.disabled);
        control.disabled = true;
      }));
    } else {
      disabledControls.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
      disabledControls.clear();
    }
    document.dispatchEvent(new CustomEvent('wb:frequency-state'));
  };
  const pendingOperation = () => {
    try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { return null; }
  };
  // A reload during the two-phase replacement must reconcile before checkout.
  if (pendingOperation()) lock(true);
  ['click', 'change', 'submit'].forEach(type => document.addEventListener(type, event => {
    const target = event.target;
    if (busy && !target.closest('[data-bundle-retry]') && (target.closest(cartArea) || target.closest('a[href*="/checkout"], [name="checkout"]'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true));

  const detailsFor = id => {
    if (!detailsCache.has(id)) {
      const locale = (window.Shopify?.locale || 'es').split('-')[0];
      const request = fetch(`https://api-service.loopwork.co/bundleTransaction/getBundleCartDetails/${encodeURIComponent(id)}?locale=${encodeURIComponent(locale)}`, { signal: AbortSignal.timeout(6000) })
        .then(response => {
          if (!response.ok) throw new Error('Loop metadata unavailable');
          return response.json();
        })
        .catch(error => { detailsCache.delete(id); throw error; });
      detailsCache.set(id, request);
    }
    return detailsCache.get(id);
  };

  const cartRequest = async (path, options = {}) => {
    const response = await fetch(root() + path, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  };

  const regularOptions = element => (element.dataset.sellingPlans || '').split('|||').filter(Boolean).map(pair => {
    const separator = pair.indexOf('::');
    return { id: pair.slice(0, separator), name: pair.slice(separator + 2).trim() };
  });
  const subscriptions = mini => [...mini.querySelectorAll('[data-cart-item][data-selling-plan], wb-loop-bundle[data-current-plan]')]
    .filter(element => element.dataset.sellingPlan || element.dataset.currentPlan);
  const currentName = element => (element.dataset.sellingPlanName || element.dataset.currentPlanName || '').trim();
  const optionsFor = element => element.matches('wb-loop-bundle') ? element.model?.plans : regularOptions(element);
  const frequencyError = (mini, message) => {
    const error = mini.querySelector('[data-global-freq-error]');
    if (error) { error.textContent = message; error.hidden = !message; }
  };
  const syncFrequency = mini => {
    if (!mini) return;
    const select = mini.querySelector('[data-global-freq]');
    if (!select) return;
    const items = subscriptions(mini);
    const bundles = mini.querySelectorAll('wb-loop-bundle');
    if (!bundles.length) {
      delete mini.dataset.globalFrequencyBlocked;
      document.dispatchEvent(new CustomEvent('wb:frequency-state'));
      return;
    }
    const wrap = mini.querySelector('[data-global-freq-wrap]');
    if (wrap) wrap.style.display = items.length ? '' : 'none';
    if (!items.length) {
      delete mini.dataset.globalFrequencyBlocked;
      delete mini.dataset.globalFrequencyError;
      frequencyError(mini, '');
      document.dispatchEvent(new CustomEvent('wb:frequency-state'));
      return;
    }
    const names = new Set(items.map(currentName));
    const lists = items.map(optionsFor);
    const loading = lists.some(list => !list?.length);
    const common = loading ? [] : lists[0].filter(plan => lists.every(list => list.some(p => p.name.trim() === plan.name.trim())));
    const actualName = names.size === 1 ? [...names][0] : '';
    const options = common.map(plan => new Option(plan.name.trim(), plan.id));
    if (!actualName || !common.some(plan => plan.name.trim() === actualName)) {
      const placeholder = new Option(actualName || 'Selecciona una frecuencia común', '');
      placeholder.disabled = true;
      placeholder.selected = true;
      options.unshift(placeholder);
    }
    select.replaceChildren(...options);
    const actual = [...select.options].find(option => option.textContent === actualName);
    if (actual) select.value = actual.value;
    select.disabled = busy || loading || !common.length;
    mini.dataset.globalFrequencyBlocked = String(names.size > 1 || mini.dataset.globalFrequencyError === 'true');
    if (mini.dataset.globalFrequencyError !== 'true') frequencyError(mini,
      loading ? (items.some(item => item.dataset.purchaseState === 'unavailable') ? 'No se han podido cargar las frecuencias del pack. Recarga el carrito.' : 'Cargando las frecuencias del pack...') : !common.length ? 'No hay una frecuencia compatible con todas las suscripciones.' : names.size > 1 ? 'Selecciona una frecuencia común para todas las suscripciones.' : '');
    document.dispatchEvent(new CustomEvent('wb:frequency-state'));
  };

  const changeFrequency = async (mini, name) => {
    if (busy || removing || !name) { syncFrequency(mini); return; }
    const owner = mini.querySelector('wb-loop-bundle');
    if (!owner) return;
    lock(true);
    delete mini.dataset.globalFrequencyError;
    frequencyError(mini, '');
    let failed = false;
    try {
      const service = await owner.service();
      const changes = [];
      const regularPlans = {};
      for (const element of subscriptions(mini)) {
        if (element.matches('wb-loop-bundle')) {
          if (element.dataset.preset !== 'true') throw new Error('Unsupported bundle');
          const details = element.details || await detailsFor(element.dataset.bundleId);
          const model = await service.load(details, element.dataset.bundleId);
          const plan = model.plans.find(plan => plan.name.trim() === name);
          if (!plan) throw new Error('Frequency unavailable');
          changes.push({ id: element.dataset.bundleId, model, plan: String(plan.id) });
        } else {
          const plan = regularOptions(element).find(plan => plan.name === name);
          if (!plan) throw new Error('Frequency unavailable');
          regularPlans[element.dataset.variantId] = plan.id;
        }
      }
      const { changeCartFrequency } = await import(owner.dataset.purchaseModule);
      const cart = await changeCartFrequency(service, changes, regularPlans, {
        pending: operation => sessionStorage.setItem(pendingKey, JSON.stringify(operation)),
        settled: () => sessionStorage.removeItem(pendingKey)
      });
      purchaseCache.clear();
      await owner.refresh(cart);
    } catch {
      failed = true;
      if (pendingOperation()) owner.showPurchaseError(true);
      else {
        try {
          purchaseCache.clear();
          const actual = await cartRequest('cart.js');
          failed = !actual.items.filter(item => item.selling_plan_allocation).every(item => item.selling_plan_allocation.selling_plan.name.trim() === name);
          await owner.refresh(actual);
        } catch { /* Leave checkout blocked until the actual cart can be read. */ }
      }
      if (failed) mini.dataset.globalFrequencyError = 'true';
    } finally {
      if (!pendingOperation()) lock(false);
      syncFrequency(mini);
      if (failed) frequencyError(mini, 'No se han podido actualizar todas las suscripciones. Revisa el calendario y vuelve a seleccionar la frecuencia.');
    }
  };

  window.WBLoopCart = { syncFrequency, changeFrequency };

  class LoopBundle extends HTMLElement {
    get scope() { return this.closest('.wb-loop-bundle-row') || this; }
    get view() { return this.closest('#t4s-mini_cart, .t4s-cartPage__items') || this.parentElement; }
    get members() {
      if (!this.model) return [this];
      return [...this.view.querySelectorAll('wb-loop-bundle')].filter(element => element.model?.groupKey === this.model.groupKey);
    }
    get quantity() { return this.members.reduce((sum, element) => sum + (element.model?.quantity || 0), 0); }

    syncGroup() {
      const members = this.members;
      const owner = members[0];
      if (!owner) return;
      members.forEach(element => element.scope.toggleAttribute('data-bundle-merged', element !== owner));
      const quantity = owner.quantity;
      owner.scope.querySelector('[data-bundle-quantity]').hidden = true;
      owner.scope.querySelector('[data-bundle-quantity-controls]').hidden = false;
      owner.scope.querySelector('[data-bundle-quantity-input]').value = quantity;
      const total = members.reduce((sum, element) => sum + Number(element.dataset.price), 0);
      const compare = members.reduce((sum, element) => sum + Number(element.dataset.comparePrice), 0);
      owner.scope.querySelector('[data-bundle-total]').textContent = owner.formatMoney(total);
      owner.renderUnitPrice(total / quantity, compare / quantity);
      owner.querySelectorAll('[data-bundle-child-quantity]').forEach(element => {
        element.textContent = `\u00d7 ${Number(element.dataset.bundleChildQuantity) / owner.model.quantity * quantity}`;
      });
      const price = owner.querySelector('[data-bundle-subscription-price]');
      if (price) {
        const format = value => owner.formatMoney(Math.round(value * quantity * 100));
        const subscriptionPrice = owner.model.variant.sellingPlanPrices[owner.model.currentPlan || owner.model.defaultPlan];
        owner.querySelector('[data-bundle-onetime-price]').textContent = format(owner.model.variant.oneTimePrice);
        price.textContent = format(subscriptionPrice);
        price.parentElement.hidden = subscriptionPrice >= owner.model.variant.oneTimePrice;
      }
    }

    renderUnitPrice(price, comparePrice) {
      const unit = this.scope.querySelector('[data-bundle-unit-price]');
      if (!unit) return;
      unit.replaceChildren();
      if (comparePrice > price) {
        const compare = document.createElement('del');
        compare.textContent = this.formatMoney(comparePrice);
        const current = document.createElement(this.matches('.wb-loop-bundle--page') ? 'ins' : 'span');
        current.textContent = this.formatMoney(price);
        unit.append(compare, document.createTextNode(' '), current);
      } else unit.textContent = this.formatMoney(price);
    }

    formatMoney(cents) {
      const currency = window.T4SThemeSP?.Currency;
      if (currency?.formatMoney) {
        const template = document.createElement('template');
        template.innerHTML = currency.formatMoney(cents);
        return template.content.textContent;
      }
      return new Intl.NumberFormat(window.Shopify?.locale || 'es', { style: 'currency', currency: this.dataset.currency || window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const remove = this.querySelector('[data-bundle-remove]');
      remove?.addEventListener('click', event => { event.preventDefault(); this.removeBundle(); });
      remove?.addEventListener('keydown', event => {
        if (event.key === ' ') { event.preventDefault(); this.removeBundle(); }
      });
      if (pendingOperation() && !recoveryStarted) {
        recoveryStarted = true;
        this.retryPending();
      }
      if (validId(this.dataset.bundleId)) this.enhance();
      this.querySelector('[data-bundle-subscribe]')?.addEventListener('change', () => {
        const subscribing = this.querySelector('[data-bundle-subscribe]').checked;
        let plan = this.model?.defaultPlan;
        const mini = this.closest('#t4s-mini_cart');
        if (subscribing && mini && subscriptions(mini).length) {
          const select = mini.querySelector('[data-global-freq]');
          plan = select?.value && this.model?.plans.find(plan => plan.name.trim() === select.selectedOptions[0]?.textContent.trim())?.id;
          if (!plan) { this.resetPurchase(); frequencyError(mini, 'Selecciona una frecuencia compatible en el calendario de entrega antes de suscribir el pack.'); return; }
        }
        this.changePurchase(subscribing ? plan : '');
      });
      this.querySelector('[data-bundle-frequency]')?.addEventListener('change', event => this.changePurchase(event.target.value));
      // Page quantities live in a sibling table cell, outside the custom element.
      this.scope.addEventListener('click', event => {
        const decrease = event.target.closest('[data-bundle-decrease]');
        if (decrease || event.target.closest('[data-bundle-increase]')) {
          event.preventDefault();
          this.changeQuantity(this.quantity + (decrease ? -1 : 1));
        }
      });
      this.scope.addEventListener('change', event => {
        if (event.target.matches('[data-bundle-quantity-input]')) this.changeQuantity(Number(event.target.value));
      });
      this.scope.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-bundle-quantity-input]')) {
          event.preventDefault();
          this.changeQuantity(Number(event.target.value));
        }
      });
    }

    async enhance() {
      try {
        const details = await detailsFor(this.dataset.bundleId);
        if (!this.isConnected) return;
        this.details = details;
        const title = this.querySelector('[data-bundle-title]');
        if (details.label) title.textContent = details.label;
        let link;
        if (this.dataset.preset === 'true' && details.product_handle) {
          link = `${root()}products/${encodeURIComponent(details.product_handle)}`;
        } else if (validId(details.loopBundleId)) {
          link = `${root()}a/loop_subscriptions/bundle/${encodeURIComponent(details.loopBundleId)}`;
        }
        if (link) this.querySelectorAll('[data-bundle-link]').forEach(el => { el.href = link; });
        if (details.image) {
          const imageUrl = new URL(details.image, location.origin);
          if (imageUrl.protocol === 'https:') {
            const image = new Image(96, 96);
            image.alt = title.textContent;
            image.onload = () => {
              if (this.isConnected) this.querySelector('.wb-loop-bundle__image').replaceChildren(image);
            };
            image.src = imageUrl.href;
          }
        }
        const quantity = Number(details.productsQuantity);
        if (Number.isInteger(quantity) && quantity > 0) {
          this.scope.querySelector('[data-bundle-quantity]').textContent = `${quantity} ${quantity === 1 ? 'pack' : 'packs'}`;
          this.renderUnitPrice(Number(this.dataset.price) / quantity, Number(this.dataset.comparePrice) / quantity);
        }
        if (this.dataset.preset !== 'true') {
          const edit = this.querySelector('[data-bundle-edit]');
          edit.href = `${root()}a/loop_subscriptions/bundle/change/${encodeURIComponent(this.dataset.bundleId)}`;
          edit.hidden = false;
        } else {
          await this.initPurchase();
        }
      } catch {
        // Server-rendered contents and Shopify prices remain usable without Loop.
        this.dataset.purchaseState = 'unavailable';
        syncFrequency(this.closest('#t4s-mini_cart'));
      }
    }

    async service() {
      if (!this.purchaseService) {
        const { PresetBundleService } = await import(this.dataset.purchaseModule);
        this.purchaseService = new PresetBundleService({ shop: this.dataset.shop, root: root(), currency: this.dataset.currency });
      }
      return this.purchaseService;
    }

    async initPurchase() {
      const service = await this.service();
      if (pendingOperation()) return;
      const id = this.dataset.bundleId;
      if (!purchaseCache.has(id)) {
        purchaseCache.set(id, service.load(this.details, id).catch(error => { purchaseCache.delete(id); throw error; }));
      }
      this.model = await purchaseCache.get(id);
      if (!this.isConnected) return;
      const select = this.querySelector('[data-bundle-frequency]');
      select?.replaceChildren(...this.model.plans.map(plan => new Option(plan.options?.[0]?.value?.trim() || plan.name, plan.id)));
      this.resetPurchase();
      this.querySelector('[data-bundle-purchase]').hidden = false;
      this.querySelector('[data-bundle-plan-label]').hidden = true;
      this.syncGroup();
      if (busy) lock(true);
      syncFrequency(this.closest('#t4s-mini_cart'));
    }

    resetPurchase() {
      const plan = this.dataset.currentPlan || '';
      this.querySelector('[data-bundle-subscribe]').checked = Boolean(plan);
      const select = this.querySelector('[data-bundle-frequency]');
      if (select) select.value = plan || this.model?.defaultPlan || '';
      const wrap = this.querySelector('[data-bundle-frequency-wrap]');
      if (wrap) wrap.hidden = !plan;
    }

    async refresh(cart) {
      if (document.querySelector('.t4s-cartPage__items')) { location.reload(); return; }
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Cart refresh timeout')), 12000);
          document.dispatchEvent(new CustomEvent('wb:bundle-updated', { detail: {
            cart,
            complete: () => { clearTimeout(timeout); resolve(); },
            fail: () => { clearTimeout(timeout); reject(new Error('Cart refresh failed')); }
          } }));
        });
      } catch { location.reload(); }
    }

    showPurchaseError(recovery = false) {
      const error = this.querySelector('[data-bundle-error]');
      error.replaceChildren(document.createTextNode(recovery
        ? 'No se ha podido confirmar el cambio. Reintenta antes de continuar.'
        : 'No se ha podido cambiar el pack. Recarga el carrito e inténtalo de nuevo.'));
      if (recovery) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.dataset.bundleRetry = '';
        retry.textContent = 'Reintentar';
        retry.addEventListener('click', () => this.retryPending());
        error.append(retry);
      }
      error.hidden = false;
    }

    async retryPending() {
      const operation = pendingOperation();
      if (!operation) return;
      lock(true);
      this.querySelectorAll('[data-bundle-retry]').forEach(button => { button.disabled = true; });
      try {
        await (await this.service()).recover(operation);
        sessionStorage.removeItem(pendingKey);
        location.reload();
      } catch { this.showPurchaseError(true); }
    }

    changeQuantity(quantity) {
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity === this.quantity) {
        this.syncGroup();
        return;
      }
      this.changePurchase(this.dataset.currentPlan || '', quantity);
    }

    async changePurchase(plan, quantity = this.quantity) {
      if (busy || removing || !this.model) { this.resetPurchase(); return; }
      lock(true);
      this.querySelector('[data-bundle-error]').hidden = true;
      try {
        const service = await this.service();
        // Revalidate current plans, contents and discount configuration on every edit.
        const entries = [];
        for (const element of this.members) {
          const model = await service.load(element.details, element.dataset.bundleId);
          if (model.groupKey !== element.model.groupKey || model.initialShape !== element.model.initialShape) throw new Error('Cart changed');
          entries.push({ id: element.dataset.bundleId, model });
        }
        const cart = await service.replaceGroup(entries, plan, quantity, {
          pending: operation => sessionStorage.setItem(pendingKey, JSON.stringify(operation)),
          settled: () => sessionStorage.removeItem(pendingKey)
        });
        purchaseCache.clear();
        await this.refresh(cart);
      } catch {
        this.resetPurchase();
        this.syncGroup();
        this.showPurchaseError(Boolean(pendingOperation()));
      } finally {
        if (!pendingOperation()) lock(false);
        syncFrequency(document.querySelector('#t4s-mini_cart'));
      }
    }

    async removeBundle() {
      if (removing || busy) return;
      if (this.model) { await this.changePurchase(this.dataset.currentPlan || '', 0); return; }
      removing = true;
      lock(true);
      const buttons = document.querySelectorAll('[data-bundle-remove]');
      buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); });
      const error = this.querySelector('[data-bundle-error]');
      error.hidden = true;
      try {
        // Read fresh keys: Shopify may change them when a discount is applied.
        const cart = await cartRequest('cart.js');
        await (await this.service()).removeTransactions(cart, [this.dataset.bundleId]);
        location.reload();
      } catch {
        error.textContent = 'No se ha podido eliminar el pack. Inténtalo de nuevo.';
        error.hidden = false;
        buttons.forEach(button => { button.disabled = false; button.removeAttribute('aria-disabled'); });
        removing = false;
        lock(false);
      }
    }
  }

  customElements.define('wb-loop-bundle', LoopBundle);
  syncFrequency(document.querySelector('#t4s-mini_cart'));
  if (pendingOperation() && !document.querySelector('wb-loop-bundle')) {
    const recovery = document.createElement('wb-loop-bundle');
    recovery.dataset.shop = scriptData.shop;
    recovery.dataset.purchaseModule = scriptData.purchaseModule;
    const message = document.createElement('p');
    message.dataset.bundleError = '';
    message.setAttribute('role', 'alert');
    message.textContent = 'Comprobando el cambio del pack...';
    recovery.append(message);
    document.body.prepend(recovery);
  }
})();
